import { useEffect, useState } from 'react';

import { ListItemText, Menu, MenuItem } from '@mui/material';

import PacingMetricStatusChip from './PacingMetricStatusChip';
import { pacingMetricsStatuses, PacingMetricStatus } from '../../schemas';

import { FILTER_DEBOUNCE_TIME_MS } from '@/app/constants';
import FilterButton from '@/features/trafficking/components/filters/FilterButton';
import { useFilterAnchor } from '@/features/trafficking/components/filters/useFilterAnchor';
import { Checkbox } from '@/shared/components/checkbox';

interface PacingMetricsStatusFilterProps {
  filterId: string;
  buttonAriaLabel: string;
  serachParamStatuses: PacingMetricStatus[] | undefined;
  handleNavigation: (
    selectedStatuses: PacingMetricStatus[] | undefined
  ) => void;
}

const PacingMetricsStatusFilter = ({
  filterId,
  buttonAriaLabel,
  serachParamStatuses,
  handleNavigation,
}: PacingMetricsStatusFilterProps) => {
  const { anchorEl, handleOpen, handleClose, open, popoverId } =
    useFilterAnchor(filterId);

  const [selectedStatuses, setSelectedStatuses] = useState<
    PacingMetricStatus[] | undefined
  >(serachParamStatuses);
  const [isChangedInternally, setIsChangedInternally] = useState(false);

  const handleStatusChange = (status: PacingMetricStatus) => {
    setIsChangedInternally(true);

    setSelectedStatuses((prev) => {
      const statuses = prev ?? [];
      if (statuses.includes(status)) {
        const filteredStatuses = statuses.filter((s) => s !== status);
        return filteredStatuses.length > 0 ? filteredStatuses : undefined;
      } else {
        return [...statuses, status];
      }
    });
  };

  const handleSelectAll = () => {
    setIsChangedInternally(true);

    const isAllSelected = pacingMetricsStatuses.every((status) =>
      selectedStatuses?.includes(status)
    );

    if (isAllSelected) {
      setSelectedStatuses(undefined);
    } else {
      setSelectedStatuses([...pacingMetricsStatuses]);
    }
  };

  const isAllSelected = pacingMetricsStatuses.every((status) =>
    selectedStatuses?.includes(status)
  );

  useEffect(() => {
    setSelectedStatuses(serachParamStatuses);
    setIsChangedInternally(false);
  }, [serachParamStatuses]);

  useEffect(() => {
    if (!isChangedInternally) {
      return;
    }

    const timer = setTimeout(() => {
      setIsChangedInternally(false);
      handleNavigation(selectedStatuses);
    }, FILTER_DEBOUNCE_TIME_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [handleNavigation, isChangedInternally, selectedStatuses]);

  return (
    <>
      <FilterButton
        isActive={!!selectedStatuses?.length}
        onClick={handleOpen}
        aria-label={buttonAriaLabel}
      />
      <Menu
        id={popoverId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem
          key='select-all'
          onClick={handleSelectAll}
          disableGutters
          sx={{
            py: 1.25,
            pr: 1.75,
            pl: 1,
          }}
        >
          <Checkbox
            id='select-all-algorithms'
            checked={isAllSelected}
            indeterminate={
              !isAllSelected && (selectedStatuses?.length ?? 0) > 0
            }
          />
          <ListItemText
            primary='(Select All)'
            slotProps={{
              primary: { variant: 'body3', color: 'textTertiary' },
            }}
          />
        </MenuItem>
        {pacingMetricsStatuses.map((status) => (
          <MenuItem
            key={status}
            onClick={() => {
              handleStatusChange(status);
            }}
            disableGutters
            sx={{
              py: 1.25,
              pr: 1.75,
              pl: 1,
            }}
          >
            <Checkbox
              id={status}
              checked={selectedStatuses?.includes(status) ?? false}
            />

            <PacingMetricStatusChip
              starvationBidderApplied
              status={status}
              disableTooltip
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PacingMetricsStatusFilter;
